// src/context/AuthContext.jsx
import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, LogOut, Timer } from "lucide-react";
import { api, clearAuthToken, getAuthToken } from "../services/api";

// Avaimet, joihin backend tai api-client mahdollisesti tallettaa tokenin exp-ajan (ms)
const EXPIRY_KEYS = ["accessTokenExpiresAt", "access_token_expires_at", "token_expires_at"];

// Merkitään, onko tällä selaimella ollut kirjautunut sessio aiemmin
const HAD_SESSION_KEY = "bbx_had_session";

// Mahdollinen FE-laskuri tms. – nollataan reloginissa varmuuden vuoksi
const LOGIN_FAILED_COUNT_KEY = "bbx_login_fail_count";

// ---------------------------------------------
// Idle-timeout hallintapaneeliin
// - 15 min ilman aktiivisuutta => uloskirjaus
// - varoitus 2 min ennen timeoutia
// ---------------------------------------------
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;
const IDLE_WARNING_MS = 2 * 60 * 1000;

// ---------------------------------------------
// Login-URL sovellukseen (EI WordPress wp-login)
// ---------------------------------------------
const RAW_LOGIN_URL =
  (typeof window !== "undefined" && (window.__BBX_LOGIN_URL__ || window.__LOGIN_URL__)) ||
  (typeof process !== "undefined" && process.env && process.env.REACT_APP_LOGIN_URL) ||
  "/kirjaudu";
  
const LOGIN_URL = String(RAW_LOGIN_URL).trim();

// Siivoa kaiken käyttäjäkohtaisen FE-muistin logoutissa
function clearAppStorage() {
  try {
    // Poista appin omat localStorage-avaimet, mutta säästä teema tms.
    const keep = new Set(["theme", "chakra-ui-color-mode"]);
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k) continue;
      // laajenna: siivoa myös bbx_-avaimet (mm. bbx_user_id)
      const isAppKey = /^(bb_|bb:|bbx_|bilebox:|bilebox_|buukkeri:|buukkeri_)/i.test(k) || EXPIRY_KEYS.includes(k);
      if (isAppKey && !keep.has(k)) toRemove.push(k);
    }
    // varmuuden vuoksi poista käyttäjätunnisteavain eksplisiittisesti
    if (!keep.has("bbx_user_id")) toRemove.push("bbx_user_id");
    // nollaa myös mahdollinen kirjautumisyritysten laskuri
    toRemove.push(LOGIN_FAILED_COUNT_KEY);
    toRemove.forEach((k) => localStorage.removeItem(k));

    // Siivoa vain appin omat sessionStorage-avaimet
    try {
      const ssRemove = [];
      for (let i = 0; i < sessionStorage.length; i += 1) {
        const k = sessionStorage.key(i);
        if (!k) continue;
        const isAppKey =
          /^(bb_|bb:|bbx_|bilebox:|bilebox_|buukkeri:|buukkeri_)/i.test(k) ||
          EXPIRY_KEYS.includes(k);
        if (isAppKey) ssRemove.push(k);
      }
      ssRemove.forEach((k) => sessionStorage.removeItem(k));
    } catch {}
  } catch {}

  // Tyhjennä vain Buukkerin nimiset cachet – ei WP:n cacheja
  if (typeof caches !== "undefined" && caches?.keys) {
    caches.keys()
      .then((names) => {
        names
          .filter((n) => /^bb|^buukkeri|^bilebox/i.test(n))
          .forEach((n) => { try { caches.delete(n); } catch {} });
      })
      .catch(() => {});
  }

  // Unregoi vain Buukkerin SW:t rajatusta scopesta
  try {
    if (typeof navigator !== "undefined" && navigator.serviceWorker?.getRegistrations) {
      navigator.serviceWorker
        .getRegistrations()
        .then((regs) =>
          Promise.all(
            regs
              .filter((r) => r.scope && /\/(app|esiintyja|kirjaudu)\b/.test(r.scope))
              .map((r) => r.unregister().catch(() => {}))
          )
        )
        .catch(() => {});
    }
  } catch {}
}

const AuthContext = createContext({
  user: null,
  currentUser: null,
  isAuthenticated: false,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

// Teemaan istuva modali (sama ulkoilme/animointi kuin BookingModalissa)
function SessionExpiredModal({ open, onRelogin, onClose, title, description, continueLabel, reloginLabel, onContinue }) {
  const reloginRef = useRef(null); // fokusoidaan CTA:han
  // Lukitse body-scroll modaalin ajaksi
  useEffect(() => {
    if (!open) return;
    setTimeout(() => reloginRef.current?.focus(), 0);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "Enter") (onContinue || onRelogin)?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, onRelogin, onContinue]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-black/90"
            onClick={onClose}
            role="presentation"
            aria-hidden="true"
          />

          {/* Dialog */}
          <motion.div
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="session-expired-title"
            aria-describedby="session-expired-desc"
            tabIndex={-1}
            className="
              relative z-[1] w-[92%] sm:w-[90%] max-w-md
              rounded-2xl ring-1 backdrop-blur-sm shadow-xl
              px-4 sm:px-5 pt-5 pb-4
              bg-lemon/90 ring-black/10
              dark:bg-night-700/80 dark:ring-night-500/50
            "
            initial={{ y: 40, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 20, scale: 0.98, opacity: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            {/* Close */}
            <button
              onClick={onClose}
              aria-label="Sulje"
              type="button"
              className="absolute top-2 right-2 inline-flex h-9 w-9 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-black dark:text-white hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="flex items-center gap-3 pr-10">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-pink/90 text-white shadow">
                <Timer className="h-5 w-5" />
              </div>
              <div>
                <h3
                  id="session-expired-title"
                  className="font-customFont2 text-xl sm:text-2xl font-bold text-black/90 dark:text-white/90"
                >
                  {title || "Istunto päättyi"}
                </h3>
                <p id="session-expired-desc" className="mt-0.5 font-customFont1 text-sm sm:text-[15px] text-black/80 dark:text-white/75">
                  {description || "Kirjautumisesi on vanhentunut. Kirjaudu uudelleen jatkaaksesi."}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-4 flex items-center justify-end gap-2 border-t border-white/10 pt-3">
              {onContinue ? (
                <button
                  onClick={onContinue}
                  type="button"
                  className="px-3 py-2 rounded-full text-sm
                             ring-1 ring-black/10 text-black/80 bg-white/70 hover:bg-white
                             dark:bg-white/10 dark:text-white/85 dark:ring-white/15"
                >
                  {continueLabel || "Jatka istuntoa"}
                </button>
              ) : (
                <button
                  onClick={onClose}
                  type="button"
                  className="px-3 py-2 rounded-full text-sm
                             ring-1 ring-black/10 text-black/80 bg-white/70 hover:bg-white
                             dark:bg-white/10 dark:text-white/85 dark:ring-white/15"
                >
                  Sulje
                </button>
              )}
              <button
                onClick={onRelogin}
                type="button"
                ref={reloginRef}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-sm
            bg-pink/90 text-white hover:bg-pink
            ring-1 ring-pink/40 focus:outline-none focus:ring-2 focus:ring-pink/60"
              >
                <LogOut className="h-4 w-4" />
                {reloginLabel || "Kirjaudu uudelleen"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Varmistaa, ettei vanhat /me-/refresh-vastaukset voi yliajaa uutta sessiota (race-condition-suoja)
  const authEpochRef = useRef(0);

  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [idleModalOpen, setIdleModalOpen] = useState(false);
  const suppressModalRef = useRef(false);    // estä modali kun relogataan
  const reloggingRef = useRef(false);        // tuplaklikkien esto
  const lastActivityRef = useRef(Date.now());
  const idleWarnedRef = useRef(false);
  // (poistettu: hadSessionRef – korvattu authedThisSessionRef:llä)
  // ✨ UUSI: modali sallitaan vain, jos käyttäjä on kirjautunut tämän SPA-sesion aikana
  const authedThisSessionRef = useRef(false);

  // ✅ Näytä muistutus vain kerran, kunnes kirjaudutaan takaisin
  const remindedOnceRef = useRef(false);
  const openModalOnce = useCallback(() => {
    if (remindedOnceRef.current) return;     // jo muistutettu → älä avaa uudestaan
    setSessionModalOpen(true);
    remindedOnceRef.current = true;
  }, []);

  // ✅ Normalisoi exp (sekunnit→ms), suodata oudot arvot ja palauta MAKSIMI
  const readExpiryMs = useCallback(() => {
    const now = Date.now();
    const CUTOFF_PAST = now - 5 * 60_000;                    // hylkää reilusti menneet
    const CUTOFF_FUT  = now + 365 * 24 * 60 * 60_000;        // hylkää epärealistisen kaukana
    const normalize = (raw) => {
      if (raw == null) return null;
      const n = Number(raw);
      if (!Number.isFinite(n) || n <= 0) return null;
      const ms = n < 1e11 ? Math.round(n * 1000) : Math.round(n); // 10-num → s, 13-num → ms
      if (ms < CUTOFF_PAST || ms > CUTOFF_FUT) return null;
      return ms;
    };
    const vals = [];
    for (const k of EXPIRY_KEYS) {
      try {
        const v = normalize(localStorage.getItem(k));
        if (v) vals.push(v);
      } catch {}
    }
    if (vals.length === 0) return null;
    return Math.max(...vals);
  }, []);

    const refresh = useCallback(async () => {
    const myEpoch = authEpochRef.current; // snapshot

    // Vierailijalla ei ole access tokenia, joten /auth/me-kutsua ei tarvitse tehdä.
    // Tämä poistaa etusivun turhan 401 /auth/me -pyynnön anonyymeiltä käyttäjiltä.
    if (!getAuthToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await api.auth.me();
      console.debug("[auth] /me OK:", data);
      // ✅ Jos logout/login ehti tapahtua tämän pyynnön aikana, älä koske stateen
      if (authEpochRef.current !== myEpoch) return;
      // ✅ Salli eri vastausmuodot:
      // - { user: {...} }
      // - { currentUser: {...} }
      // - { me: {...} }
      // - tai käyttäjä suoraan {...}
      const wrapped =
        data?.user ?? data?.currentUser ?? data?.me ?? null;
      const direct =
        data && (data.id || data._id || data.user_id || data.email) ? data : null;
      const nextUser = wrapped ?? direct ?? null;

      setUser(nextUser);

      if (nextUser) {
        authedThisSessionRef.current = true;     // ✅ kirjautui TÄSSÄ sesiossa
        lastActivityRef.current = Date.now();
        idleWarnedRef.current = false;
        // 🔄 Kirjauduttiin takaisin → nollaa kertamuistutus
        remindedOnceRef.current = false;
        try { localStorage.setItem(HAD_SESSION_KEY, "1"); } catch {}
      }

      // Jos käyttäjä vaihtui, siivoa clientin välimuistit ja nimeä avaimet uudelleen
      try {
        const prevId = localStorage.getItem('bbx_user_id');
        const newId  = nextUser?.id ? String(nextUser.id) : null;
        if (prevId && newId && prevId !== newId) {
          clearAppStorage();
        }
        if (newId) localStorage.setItem('bbx_user_id', newId);
        else localStorage.removeItem('bbx_user_id');
      } catch {}

      readExpiryMs();
    } catch (e) {
      console.debug("[auth] /me FAIL:", e?.status, e?.payload || e?.message);
      // ✅ Sama suoja myös virhepolussa (vanha pyyntö ei saa tyhjätä uutta sessiota)
      if (authEpochRef.current !== myEpoch) return;
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [readExpiryMs]);

const logout = useCallback(
  async ({ redirect = true, hard = true } = {}) => {
    // ✅ Invalidoi kaikki käynnissä olevat auth-refreshit tms.
    authEpochRef.current += 1;

    // Katkaise kaikki keskeneräiset API-pyynnöt (jos tuki on lisätty apiin)
    try { if (typeof api?.abortAll === "function") api.abortAll(); } catch {}

    // 1) Ilmoita backendille ensin ja odota logoutin valmistumista.
    // Tämä vähentää race condition -tilanteita, joissa seuraava auth-bootstrap
    // ehtii käynnistyä ennen kuin cookiet on oikeasti tyhjennetty.
    try {
      if (api?.auth?.logout) {
        await api.auth.logout();
      }
    } catch {
      // ei haittaa vaikka kaatuisi
    }

    // 2) Siivotaan FE heti → käyttäjä näkee muutoksen saman tien
    try { clearAuthToken(); } catch {}

    if (typeof api?.reset === "function") {
      try { api.reset(); } catch {}
    }

    if (hard) clearAppStorage();

    setUser(null);
    setIdleModalOpen(false);
    authedThisSessionRef.current = false;
    idleWarnedRef.current = false;
    remindedOnceRef.current = false;

    try { localStorage.removeItem(HAD_SESSION_KEY); } catch {}
    try { localStorage.setItem("bb_auth_logout", String(Date.now())); } catch {}

    if (redirect) {
      navigate(LOGIN_URL, { replace: true });
    }
  },
  [navigate]
);



  // Polkuapu: ollaanko jo esiintyjien login-sivulla?
  const isLoginPath = useCallback(() => {
    if (typeof window === "undefined") return false;
    try {
      const base = window.location.origin || "";
      const u = new URL(LOGIN_URL, base);
      const want = (u.pathname || "/kirjaudu").replace(/\/+$/,"");
      const here = (window.location.pathname || "").replace(/\/+$/,"");
      return here === want || here.startsWith(want + "/");
    } catch {
      return false;
    }
  }, []);

  // Pehmeä vanhentuminen:
  // - poista token
  // - nollaa käyttäjä heti, jotta UI ei jää "haamukirjautuneeksi"
  // - näytä relogin-modali, ellei olla jo kirjautumissivulla
  const expireSession = useCallback(() => {
    try { clearAuthToken(); } catch {}

    // ✅ tärkeä: pudota auth-state heti, jotta dashboard ei näytä enää kirjautunutta käyttäjää
    setUser(null);

    // ✅ Modali vain jos tämä SPA-sessio on ollut oikeasti kirjautuneena
    if (suppressModalRef.current || isLoginPath() || !authedThisSessionRef.current) return;
    openModalOnce();
  }, [isLoginPath, openModalOnce]);

  const continueSession = useCallback(async () => {
    lastActivityRef.current = Date.now();
    idleWarnedRef.current = false;
    setIdleModalOpen(false);
    try {
      await refresh();
    } catch {
      // jos refresh epäonnistuu, normaali relogin-flow hoitaa tilanteen myöhemmin
    }
  }, [refresh]);

   useEffect(() => {
    // ⛔️ Älä blokkaa /kirjaudu-sivua hitaalla /me-haulla
    if (isLoginPath()) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh, isLoginPath]);

  // Vahti: varoita ~30s ennen expiä ja tee pehmeä uloskirjaus expin ylittyessä
  useEffect(() => {
    const THRESHOLD_MS = 30_000;

    const tick = () => {
     if (suppressModalRef.current || isLoginPath() || !authedThisSessionRef.current) return; // ✅

      const exp = readExpiryMs();
      if (!exp) return;

      const now = Date.now();

      if (now >= exp) {
        expireSession();  // kutsuu openModalOnce()
        return;
      }

      // Ennakkovaroitus: muistuta vain kerran
      if (exp - now <= THRESHOLD_MS) { // ✅ kirjautumisvartija yllä
        openModalOnce();
      } else if (user) {
        // jos ollaan kirjautuneena ja ei lähellä expiä → pidä modal kiinni
        setSessionModalOpen(false);
      }
    };

    const id = setInterval(tick, 15_000);
    tick(); // heti
    return () => clearInterval(id);
  }, [user, readExpiryMs, expireSession, isLoginPath, openModalOnce]);

  // Idle-vahti:
  // - 15 min ilman aktiivisuutta => logout
  // - varoitus 2 min ennen timeoutia
  useEffect(() => {
    if (!user || isLoginPath()) return;

    const markActivity = () => {
      lastActivityRef.current = Date.now();
      if (idleWarnedRef.current) {
        idleWarnedRef.current = false;
        setIdleModalOpen(false);
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((ev) => window.addEventListener(ev, markActivity, { passive: true }));

    const tick = () => {
      if (!authedThisSessionRef.current || suppressModalRef.current || isLoginPath()) return;

      const now = Date.now();
      const idleMs = now - lastActivityRef.current;
      const warnAt = IDLE_TIMEOUT_MS - IDLE_WARNING_MS;

      if (idleMs >= IDLE_TIMEOUT_MS) {
        setIdleModalOpen(false);
        logout({ redirect: true, hard: true });
        return;
      }

      if (idleMs >= warnAt) {
        if (!idleWarnedRef.current) {
          idleWarnedRef.current = true;
          setIdleModalOpen(true);
        }
      } else if (idleWarnedRef.current) {
        idleWarnedRef.current = false;
        setIdleModalOpen(false);
      }
    };

    const id = setInterval(tick, 10_000);
    tick();

    return () => {
      clearInterval(id);
      events.forEach((ev) => window.removeEventListener(ev, markActivity));
    };
  }, [user, isLoginPath, logout]);

  // Välilehtisynkka: storage-eventit
  useEffect(() => {
    const onStorage = (e) => {
      if (!e) return;

      // Manuaalinen logout toisessa välilehdessä → älä avaa muistutusmodalia
      if (e.key === "bb_auth_logout" && e.newValue) {
        authEpochRef.current += 1; // ✅ estä vanhoja pyyntöjä yliajamasta tässä tabissa
        setUser(null);
        authedThisSessionRef.current = false;  // ✅
        remindedOnceRef.current = false; // ei muistutusta manuaaliloggareista
        try { localStorage.removeItem(HAD_SESSION_KEY); } catch {}
        return;
      }

      // Tokenin exp-ajan päivitys toisessa välilehdessä
      if (e.key && EXPIRY_KEYS.includes(e.key)) {
        if (!authedThisSessionRef.current) return; // ✅ vierailijoille ei koskaan
        const exp = readExpiryMs();
        if (exp && exp - Date.now() <= 30_000) {
          if (!suppressModalRef.current && !isLoginPath()) openModalOnce();
        }
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [readExpiryMs, user, isLoginPath, openModalOnce]);

  // Kuuntele 401:ä api-kerroksesta (soft-expire, ei redirectiä)
  useEffect(() => {
    // ✅ Älä hälytä vierailijoille.
    // Jos 401 tuli jo /auth/me -pyynnöstä, älä kutsu refresh():ä uudelleen,
    // muuten syntyy /me -> 401 -> refresh -> /me -> 401 -looppi.
    const off = typeof api?.onUnauthorized === "function"
      ? api.onUnauthorized(async (detail = {}) => {
          if (!authedThisSessionRef.current) return;

          const failedUrl = String(detail?.url || "").toLowerCase();
          const isMeRequest =
            failedUrl.includes("/api/auth/me") ||
            failedUrl.endsWith("/auth/me");

          if (isMeRequest) {
            expireSession();
            return;
          }

          const exp = readExpiryMs();
          const soon = exp && (exp - Date.now() <= 60_000);

          if (!soon) {
            try {
              await refresh();
              return;
            } catch {}
          }

          expireSession();
        })
      : null;

    return () => {
      if (typeof off === "function") off();
    };
  }, [expireSession, readExpiryMs, refresh]);

  const handleRelogin = useCallback(() => {
    if (reloggingRef.current) return;
    reloggingRef.current = true;
    suppressModalRef.current = true;
    setSessionModalOpen(false);
    authEpochRef.current += 1; // ✅ invalidoi vanhat /me-vastaukset heti
    // Katkaise kaikki keskeneräiset API-pyynnöt (jos tuki on lisätty apiin)
    try { if (typeof api?.abortAll === "function") api.abortAll(); } catch {}

    // Jos ollaan jo kirjautumissivulla, älä ketjuta /kirjaudu?next=/kirjaudu?...
    // Jätä käyttäjä suoraan kirjautumaan (modali suljettu).
    if (isLoginPath()) {
      reloggingRef.current = false;
      return;
    }


    // 1) Minimisiivous jotta renderöinti ei blokkaannu
    try { clearAuthToken(); } catch {}
    try {
      localStorage.removeItem("bbx_user_id");
      EXPIRY_KEYS.forEach((k) => localStorage.removeItem(k));
      localStorage.removeItem(LOGIN_FAILED_COUNT_KEY);
      localStorage.setItem("bb_auth_logout", String(Date.now()));
    } catch {}

    // 2) Pudota käyttäjätilanne välittömästi → route guardit eivät odota /me-kutsua
    setUser(null);

    // 3) Valmistele kohde-URL – estä /kirjaudu?next=/kirjaudu?... -ketjutus
    const here = (location.pathname || "/") + (location.search || "") + (location.hash || "");
    const decode = (v) => { try { return decodeURIComponent(v); } catch { return v; } };
    const stripNestedNext = (v) => {
      let out = v || "/";
      // Pura korkeintaan muutama kerros varmuuden vuoksi
      for (let i = 0; i < 4; i += 1) {
        const val = decode(out);
        // Jos arvo alkaa login-polulla, kaiva sen sisältä "next"
        if (!val.startsWith(LOGIN_URL)) break;
        const q = val.split("?")[1] || "";
        const p = new URLSearchParams(q).get("next");
        if (!p) { out = "/"; break; }
        out = p;
      }
      return out;
    };
    const sanitizedNext = stripNestedNext(here);
    const target = `${LOGIN_URL}?next=${encodeURIComponent(sanitizedNext)}`;
    // 4) Nopea SPA-navigaatio heti…
    try { navigate(target, { replace: true }); } catch {}
    // …ja kovempi varmistus seuraavassa tikissä (jos jokin overlay tms. hidastaisi)
    setTimeout(() => {
      try { window.location.replace(target); }
      catch { window.location.href = target; }
    }, 0);
  }, [location, isLoginPath, navigate]);

  return (
    <AuthContext.Provider value={{ user, currentUser: user, isAuthenticated: !!user, loading, refresh, logout }}>
      {children}
      <SessionExpiredModal
        open={sessionModalOpen}
        onRelogin={handleRelogin}
        onClose={handleRelogin}
      />
      <SessionExpiredModal
        open={idleModalOpen}
        onRelogin={handleRelogin}
        onClose={handleRelogin}
        onContinue={continueSession}
        title="Istunto päättyy pian"
        description="Et ole ollut aktiivinen hetkeen. Jatka istuntoa tai kirjaudu uudelleen."
        continueLabel="Jatka istuntoa"
        reloginLabel="Kirjaudu uudelleen"
      />
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
